import { create } from 'zustand';
import type { BuiltinVariableGroup } from 'api/services/builtinVariableService';
import type {
  CatalogVariable,
  DeprecatedVariableMapping,
} from 'api/generated/types';
import { ResolutionStage } from 'api/generated/types';
import {
  toBuiltinVariableGroups,
  toDerivedVariableNameSet,
  toDerivedVariableNameSetAtOrBeforeStage,
  toJobAttributeVariableNameSet,
  toOrganizationVariableNameSet,
  toOrganizationVariableNameSetAtOrBeforeStage,
} from 'api/services/organizationVariableService';

/** Grouped built-ins for autocomplete. Org-scoped: one catalog serves every surface. */
export type BuiltinVariableCatalog = {
  groups: BuiltinVariableGroup[];
};

export const EMPTY_BUILTIN_CATALOG: BuiltinVariableCatalog = {
  groups: [],
};

const EMPTY_MAP = new Map<string, string>();
const EMPTY_SET = new Set<string>();
const EMPTY_VARIABLES: CatalogVariable[] = [];

/** Everything the catalog populates, derived from one GET /admin/variables/catalog response. */
type CatalogData = {
  /** The flat catalog as returned, for surfaces that list every variable. */
  variables: CatalogVariable[];
  organizationVariableNames: Set<string>;
  derivedVariableNames: Set<string>;
  configStageOrganizationVariableNames: Set<string>;
  configStageDerivedVariableNames: Set<string>;
  jobAttributeVariableNames: Set<string>;
  deprecatedVariables: Map<string, string>;
  builtinCatalog: BuiltinVariableCatalog;
  fetched: boolean;
};

/** Per-job editor state; reset when the editor leaves the job. */
type JobEditorData = {
  jobVariableValues: Record<string, string> | null;
  unmatchedLookupVariables: Set<string>;
  deferredVariables: Set<string>;
};

export type VariableCatalogState = CatalogData &
  JobEditorData & {
    applyCatalog: (
      variables: CatalogVariable[],
      deprecatedVariables?: DeprecatedVariableMapping[]
    ) => void;
    invalidateCatalog: () => void;
    /** Drop every catalog field. For tenant changes — `invalidateCatalog` keeps the
     * names visible while refetching, which is wrong across orgs. */
    clearCatalog: () => void;
    setJobVariableValues: (values: Record<string, string> | null) => void;
    setUnmatchedLookupVariables: (names: Set<string>) => void;
    setDeferredVariables: (names: Set<string>) => void;
  };

// Shared empty values keep their identity so selectors on an empty store do not re-render.
const INITIAL_STATE: CatalogData & JobEditorData = {
  variables: EMPTY_VARIABLES,
  organizationVariableNames: EMPTY_SET,
  derivedVariableNames: EMPTY_SET,
  configStageOrganizationVariableNames: EMPTY_SET,
  configStageDerivedVariableNames: EMPTY_SET,
  jobAttributeVariableNames: EMPTY_SET,
  deprecatedVariables: EMPTY_MAP,
  builtinCatalog: EMPTY_BUILTIN_CATALOG,
  fetched: false,
  jobVariableValues: null,
  unmatchedLookupVariables: EMPTY_SET,
  deferredVariables: EMPTY_SET,
};

const toDeprecatedVariableMap = (
  deprecatedVariables: DeprecatedVariableMapping[] | undefined
): Map<string, string> => {
  const deprecatedMap = new Map<string, string>();
  for (const item of deprecatedVariables ?? []) {
    if (item.deprecated_pattern && item.new_variable_pattern) {
      deprecatedMap.set(item.deprecated_pattern, item.new_variable_pattern);
    }
  }
  return deprecatedMap;
};

/** Pure: derive every catalog field from one flat catalog response. */
const toCatalogData = (
  variables: CatalogVariable[],
  deprecatedVariables?: DeprecatedVariableMapping[]
): CatalogData => ({
  variables,
  organizationVariableNames: toOrganizationVariableNameSet(variables),
  derivedVariableNames: toDerivedVariableNameSet(variables),
  configStageOrganizationVariableNames:
    toOrganizationVariableNameSetAtOrBeforeStage(
      variables,
      ResolutionStage.config
    ),
  configStageDerivedVariableNames: toDerivedVariableNameSetAtOrBeforeStage(
    variables,
    ResolutionStage.config
  ),
  jobAttributeVariableNames: toJobAttributeVariableNameSet(variables),
  deprecatedVariables: toDeprecatedVariableMap(deprecatedVariables),
  builtinCatalog: { groups: toBuiltinVariableGroups(variables) },
  fetched: true,
});

// Marked pure so a build that imports this module only through the session
// store, and never uses the store, drops it (see useSessionStore).
export const useVariableCatalogStore =
  /* @__PURE__ */ create<VariableCatalogState>()((set) => ({
    ...INITIAL_STATE,
    applyCatalog: (variables, deprecatedVariables) =>
      set(toCatalogData(variables, deprecatedVariables)),
    invalidateCatalog: () => set({ fetched: false }),
    clearCatalog: () => set(INITIAL_STATE),
    setJobVariableValues: (jobVariableValues) => set({ jobVariableValues }),
    setUnmatchedLookupVariables: (unmatchedLookupVariables) =>
      set({ unmatchedLookupVariables }),
    setDeferredVariables: (deferredVariables) => set({ deferredVariables }),
  }));

export const selectCatalogVariables = (s: VariableCatalogState) => s.variables;
export const selectOrganizationVariableNames = (s: VariableCatalogState) =>
  s.organizationVariableNames;
export const selectJobVariableValues = (s: VariableCatalogState) =>
  s.jobVariableValues;
export const selectJobAttributeVariableNames = (s: VariableCatalogState) =>
  s.jobAttributeVariableNames;
export const selectDeprecatedVariables = (s: VariableCatalogState) =>
  s.deprecatedVariables;
export const selectUnmatchedLookupVariables = (s: VariableCatalogState) =>
  s.unmatchedLookupVariables;
export const selectDeferredVariables = (s: VariableCatalogState) =>
  s.deferredVariables;
export const selectDerivedVariableNames = (s: VariableCatalogState) =>
  s.derivedVariableNames;
export const selectConfigStageOrganizationVariableNames = (
  s: VariableCatalogState
) => s.configStageOrganizationVariableNames;
export const selectConfigStageDerivedVariableNames = (
  s: VariableCatalogState
) => s.configStageDerivedVariableNames;
export const selectBuiltinGroups = (s: VariableCatalogState) =>
  s.builtinCatalog.groups;
export const selectFetched = (s: VariableCatalogState) => s.fetched;

/** Test-only: restore empty defaults (including EMPTY_SET identity). */
export const __resetVariableCatalogStoreForTests = (): void => {
  useVariableCatalogStore.setState(INITIAL_STATE);
};
